<template>
  <div class="map-popup">
    <div class="map-popup-header">
      <div class="icon">
        <i :class="icon" class="theme-icon" />
      </div>

      <span class="title"
            :title="title"
      >
        <slot name="title">
          {{ title }}
        </slot>
      </span>

      <ul v-if="showTabs"
          class="map-popup-menu"
      >
        <li v-if="actions && actions.length"
            class="map-popup-menu__item"
        >
          <ActionsDropdown :actions="actions">
            <i class="icon-v-dots" />
          </ActionsDropdown>
        </li>
        <li v-if="showStreetView"
            class="map-popup-menu__item"
            :class="{'active': view === STREETVIEW_VIEW }"
            @click="view = STREETVIEW_VIEW"
        >
          <i class="icon-streetview" />
        </li>
        <li class="map-popup-menu__item"
            :class="{'active': view === PARAMETERS_VIEW }"
            @click="view = PARAMETERS_VIEW"
        >
          <i class="icon-menu" />
        </li>
      </ul>

      <i v-if="callAction" class="icon-telephone action-icon control-icon" @click="emit('callAction')"></i>
      <i class="icon-times action-icon control-icon" @click="close" />
    </div>

    <div class="map-popup-body">
      <div v-if="loading || progress"
           class="content-loader"
      >
        <Spinner />
      </div>


      <template v-if="!loading">
        <template v-if="view === PARAMETERS_VIEW">
          <slot />
          <div v-for="(row, index) in data"
               :key="`primary-${row.title}-${index}`"
               class="map-popup__row"
          >
          <span class="label">
            {{ row.title }}
          </span>

            <span class="value">
            {{ row.value }}
          </span>
          </div>

          <template v-if="secondaryData && secondaryData.length">
            <Collapsable
                v-if="secondaryData"
                v-model:expanded="expanded"
            >
              <div v-for="(row, index) in secondaryData"
                   :key="`secondary-${index}-${row.title}`"
                   class="map-popup__row"
              >
              <span class="label">
                {{ row.title }}
              </span>

                <span class="value"
                      v-html="row.value"
                />
              </div>
            </Collapsable>

            <span class="more"
                  @click="expanded = !expanded"
            >
            <i class="icon-v-dots" />
          </span>
          </template>
        </template>
        <template v-else>
          <div class="streetview">
            <GoogleStreetView
                class="w-100 h-100"
                :lat="lat"
                :lng="lng"
                image-size="290x125"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
<script setup>
import Spinner from "@/js/Elements/Spinner.vue";
import Collapsable from "@/js/Elements/Collapsable.vue";
import {computed, ref} from "vue";
import {useStore} from "vuex";
import GoogleStreetView from "@/js/Elements/GoogleStreetView.vue";
import ActionsDropdown from "@/js/Elements/ActionsDropdown.vue";

const props = defineProps({
  title: String,
  icon: String,
  lat: [String, Number],
  lng: [String, Number],
  showTabs: Boolean,
  loading: Boolean,
  progress: Boolean,
  data: Array,
  secondaryData: Array,
  callAction: Boolean,
  actions: Array,
});

const PARAMETERS_VIEW = 'parameters';
const STREETVIEW_VIEW = 'streetview';
const store = useStore();
const emit = defineEmits(['close', 'callAction']);
const expanded = ref(false);
const showStreetView = computed(() => store.getters['map/settings']?.showStreetview);
const view = ref(PARAMETERS_VIEW);

function close(e) {
  e.stopPropagation();
  emit('close');
}
</script>
<style scoped lang="scss">
@import "@/css/functions";
@import "@/css/variables";

.map-popup {
  width: 100%;
  padding-bottom: spacing(4);
  min-width: toRem(320);
  max-width: toRem(320);

  .more {
    display: flex;
    justify-content: center;
    width: 100%;
    cursor: pointer;
    color: themeColor('neutral-500');
    padding: spacing(2) 0;

    i {
      transform: rotate(90deg);
      font-size: fontSize(2);
    }
  }

  &-header {
    display: flex;
    align-items: center;
    height: toRem(44);
    gap: spacing(2);
    padding-right: spacing(3);

    .icon {
      padding-left: spacing(4);
      display: flex;
      align-items: center;
      color: themeColor('primary');
      flex-shrink: 0;
      font-size: fontSize(3);
    }

    .title {
      width: 100%;
      font-size: fontSize(2);
      font-weight: $font-weight-semibold;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &-menu {
    display: flex;
    align-items: center;
    height: 100%;
    margin-bottom: 0;

    &__item {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      aspect-ratio: 1/1;
      position: relative;
      color: themeColor('neutral-500');

      &.active {
        color: themeColor('primary');
        border-bottom: themeColor('primary') solid toRem(2);
      }
    }
  }

  &-body {
    border-top: themeColor('border') solid toRem(1);
    padding-top: spacing(3);
    overflow: hidden;
    position: relative;
    min-height: toRem(50);
  }

  .streetview {
    display: flex;
    width: 100%;
    height: toRem(200);
    justify-content: center;
    align-items: center;
  }

  :deep(.map-popup__row) {
    display: flex;
    padding: spacing(1) 0;
    font-size: fontSize(5);
    color: themeColor('neutral-800');

    .label {
      width: 50%;
      flex-shrink: 0;
      padding-left: spacing(4);
      font-weight: $font-weight-semibold;
    }

    .value {
      padding-right: spacing(4);
    }

    .address {
      .spinner { margin-left: toRem(23); }
    }
  }
}
</style>